/**
 * The shape a hedge segment is drawn as: a lightly subdivided box pushed *inward* by
 * seeded noise, so the maze's walls stop being ruler-drawn without any of them growing.
 *
 * Everything here is decided by two facts about how the hedges are rendered.
 *
 * **One instanced draw.** Every hedge in the maze is the same geometry — see `MazeGrounds`.
 * So there is exactly one shape to build, it is built once at mount, and per-piece variety
 * has to come from the instance transform (a seeded half-turn) rather than from the mesh.
 *
 * **The collider is the merged box, not this.** The player is stopped by the flat boxes
 * `buildGrounds` emits; this is only what they look like. That makes displacement a
 * one-way street: a vertex may move *in* from the unit box's face, never out, or the maze
 * would show foliage sticking through a wall the player cannot reach (design D10). Every
 * inset below is a non-negative number subtracted from a face coordinate, which is the
 * cheapest possible way to make that guarantee true by construction rather than by
 * clamping afterwards.
 *
 * Two faces are pinned flat on top of that, and both are about seams rather than looks:
 *
 * - **The ends (local ±x).** `hedgeSegments` cuts a long run into ~3 m pieces that butt
 *   end to end, and `MazeGrounds` always orients the geometry so local +x runs along the
 *   hedge. Pull an end face in and the run develops a gap every three metres. So the
 *   displacement tapers to zero over the last segment before each end, which also pins the
 *   side and top lumps there — neighbouring pieces then meet with identical surfaces and
 *   the joint is invisible.
 * - **The footing (local y = -0.5).** The box's bottom sits exactly on the grass. Pull the
 *   skin in down there and the hedge is undercut, floating a few centimetres above its own
 *   shadow. So the side inset fades out over the lowest segment as well.
 *
 * What is left free is what the player actually looks at: the two long faces either side of
 * a corridor, and the clipped top edge seen against the sky.
 */

import { BoxGeometry, type BufferGeometry } from 'three';
import { mulberry32, seedFromString } from '../utils/seededRandom';

/**
 * Subdivision, in segments per local axis: `run` along the hedge, `up` its height,
 * `through` its thickness.
 *
 * 6 × 5 × 2 is 104 quads, 208 triangles — against 12 for the box it replaces. At ~167
 * drawn pieces in the shipped 12×12 maze that is about 35k triangles for the whole hedge
 * maze, in one draw call, which is the budget design D10 allowed. Going finer buys smaller
 * lumps that the toon ramp cannot band anyway; `through` stays at 2 because nothing but the
 * top roll is ever subdivided across a 0.6 m thickness.
 */
export const HEDGE_RUN_SEGMENTS = 6;
export const HEDGE_UP_SEGMENTS = 5;
export const HEDGE_THROUGH_SEGMENTS = 2;

/**
 * Maximum inset, in unit-box coordinates (the box spans -0.5..0.5, so these are fractions
 * of the piece's own size on that axis).
 *
 * They differ because the axes are not the same size in the world. A hedge is 0.6 m thick,
 * so 0.13 is a 7.8 cm bite out of a side face — enough to read as foliage at arm's length.
 * It is 3 m tall, so the top gets 0.05, which is a 15 cm dip: any more and the clipped
 * top edge starts to look chewed instead of ragged, and the crest starts eating into the
 * margin by which a hedge stands over a jumping player's eyes (design D7).
 */
export const HEDGE_SIDE_INSET = 0.13;
export const HEDGE_TOP_INSET = 0.05;

/** How far from an end, and up from the footing, displacement fades in. One segment each. */
const END_TAPER = 1 / HEDGE_RUN_SEGMENTS;
const GROUND_TAPER = 1 / HEDGE_UP_SEGMENTS;

/** Positions come out of `BoxGeometry` as exact halves, but compare like floats anyway. */
const ON_FACE = 1e-6;

/**
 * One term of the lump field: a plane wave through the unit box.
 *
 * Sines rather than lattice noise, and that is a size decision. A value-noise grid coarse
 * enough to give smooth lumps would have fewer cells than this box has vertices, so every
 * lump would land on a lattice corner and the hedges would repeat visibly at the cell
 * spacing. A few summed waves are smooth everywhere by construction, cost a handful of
 * `Math.sin` calls once at boot, and are trivially bounded — which is what lets the
 * "never outward" guarantee hold without a clamp.
 */
type Wave = { fx: number; fy: number; fz: number; phase: number; weight: number };

/** The wave sets for the side faces and the top, kept apart so they do not rhyme. */
export type HedgeLumps = { side: Wave[]; top: Wave[] };

const WAVE_WEIGHTS = [1, 0.62, 0.34];

function waves(random: () => number): Wave[] {
  return WAVE_WEIGHTS.map((weight, index) => ({
    // Low frequencies for the loudest term and higher ones for the detail, so the field is
    // one big lump with smaller ones riding on it rather than three of the same size.
    fx: 0.5 + random() * (0.7 + index * 0.9),
    fy: 0.4 + random() * (0.6 + index * 0.8),
    fz: 0.3 + random() * (0.5 + index * 0.6),
    phase: random() * Math.PI * 2,
    weight,
  }));
}

/** The seeded lump field for a hedge. Architectural rule 4: no `Math.random` anywhere. */
export function hedgeLumps(seed: string): HedgeLumps {
  const random = mulberry32(seedFromString(`hedge-lumps:${seed}`));
  return { side: waves(random), top: waves(random) };
}

/**
 * The field at a point, in 0..1 — bounded because the weighted sine sum is divided by the
 * total weight, so it can never leave -1..1 before the half-and-half remap.
 */
export function lumpField(terms: readonly Wave[], x: number, y: number, z: number): number {
  let sum = 0;
  let total = 0;
  for (const wave of terms) {
    sum += wave.weight * Math.sin(Math.PI * 2 * (wave.fx * x + wave.fy * y + wave.fz * z) + wave.phase);
    total += wave.weight;
  }
  return 0.5 + (0.5 * sum) / total;
}

/** Hermite fade over 0..edge, so lumps arrive at a taper boundary with no crease. */
function fadeIn(value: number, edge: number): number {
  const t = Math.min(1, Math.max(0, value / edge));
  return t * t * (3 - 2 * t);
}

/**
 * Where one vertex of the unit box ends up.
 *
 * Deliberately a function of the vertex's *original* position and nothing else. A
 * `BoxGeometry` does not weld its faces: the vertex on the top edge of a side face and the
 * one on the edge of the top face are two entries in the buffer at the same coordinates.
 * Displacing by position alone gives them the same answer, so the shell stays closed —
 * displacing along each face's own normal, the obvious alternative, tears it open along
 * every edge.
 */
export function displaceHedgeVertex(
  lumps: HedgeLumps,
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  const endTaper = fadeIn(0.5 - Math.abs(x), END_TAPER);
  let outY = y;
  let outZ = z;

  if (Math.abs(Math.abs(z) - 0.5) < ON_FACE) {
    const inset =
      HEDGE_SIDE_INSET * lumpField(lumps.side, x, y, z) * endTaper * fadeIn(y + 0.5, GROUND_TAPER);
    outZ = Math.sign(z) * (0.5 - inset);
  }

  if (y > 0.5 - ON_FACE) {
    outY = 0.5 - HEDGE_TOP_INSET * lumpField(lumps.top, x, y, z) * endTaper;
  }

  return [x, outY, outZ];
}

/**
 * The geometry every hedge in the maze is an instance of. Built once per mount and shared;
 * `computeVertexNormals` afterwards is what makes the lumps catch the light, and because
 * the faces are not welded it leaves the box's own edges crisp instead of rounding the
 * whole thing into a pillow.
 */
export function hedgeGeometry(seed = 'hedge'): BufferGeometry {
  const geometry = new BoxGeometry(
    1,
    1,
    1,
    HEDGE_RUN_SEGMENTS,
    HEDGE_UP_SEGMENTS,
    HEDGE_THROUGH_SEGMENTS,
  );
  const lumps = hedgeLumps(seed);
  const position = geometry.getAttribute('position');

  for (let vertex = 0; vertex < position.count; vertex += 1) {
    const [x, y, z] = displaceHedgeVertex(
      lumps,
      position.getX(vertex),
      position.getY(vertex),
      position.getZ(vertex),
    );
    position.setXYZ(vertex, x, y, z);
  }

  position.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}
